import React, {
  useEffect,
  useState,
  useRef,
  Suspense,
  useMemo,
  useCallback,
} from 'react';
import { observer } from 'mobx-react-lite';
import { Tooltip } from 'antd';
import {
  atomOneDark,
  atomOneLight,
} from '@fengkx/react-syntax-highlighter/dist/esm/styles/hljs';
import HtmlViewTemplate from '@/components/html-viewer/view-template';
import { markdownToPlainText } from '@shared/utils';
import copy from 'copy-to-clipboard';
import {
  Skeleton,
  Segmented,
  Pagination,
  ConfigProvider,
  Dropdown,
} from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import zhCN from 'antd/es/locale/zh_CN';
import { request } from '@/common';
import Other from '@/common/helper/other';
import { SVGPureIcon } from '@/ui';
import ScrollView from '@/ui/scroll-view';
import Markdown from '@/components/markdown';
import { HelperCore } from '@/common/dependencies';
import SyntaxHighlighter from '@fengkx/react-syntax-highlighter';
import './index.scss';
import ScaleBox from '../scale-box';
import { useConversationContext } from '../chat-history/context';
import QrcodeShare from '../qrcode-share';
import fullScreenModalStore from '../full-screen-modal/store';
import { useVirtualizer } from '@tanstack/react-virtual';
import CodeBlock from './components/code-block';

const enableVirtual = true; // 是否启用虚拟列表

const isWeixin = /micromessenger/i.test(navigator.userAgent);

const DOWNLOAD_ITEMS = [
  {
    key: 'ppt',
    label: '下载为PPTX',
  },
  {
    key: 'pdf',
    label: '下载为PDF',
  },
  {
    key: 'html',
    label: '下载为HTML',
  },
];

const CodeCard = React.memo(
  props => {
    const {
      index,
      msg_id,
      isPhone,
      handleCopyFile,
      status,
      content,
      darkMode = true,
    } = props;

    if (!content || !content.trim()) {
      return null;
    }

    const node = useMemo(() => {
      return (
        <div key={`${msg_id}-${index}`} className="w-full pr-2px">
          <div
            className={[
              'flex bg-transparent justify-between items-center',
              'py-8px text-[#FFF]',
              `${isPhone ? 'pl-24px' : ''}`,
              'border-b-1 border-t-1 border-[#323438]',
            ]}
          >
            <div className="px-10px py-4px border-1 border-[rgba(255,255,255,0.1)] rounded-[6px]">
              第{index + 1}页
            </div>
            <div
              className="px-10px py-4px mr-24px border-1 border-[rgba(255,255,255,0.1)] rounded-[6px] cursor-pointer"
              onClick={() => {
                handleCopyFile(content);
              }}
            >
              复制
            </div>
          </div>
          <ScrollView
            scrollbarThin={true}
            enableAutoScroll={true}
            className={`!h-250px bg-[#1E1F22]}`}
            // className="!h-250px overflow-x-hidden overflow-y-scroll nw-scrollbar-thin rounded-12px bg-[#1E1F22]"
          >
            <CodeBlock
              key={`${msg_id}-${index}`}
              language={'html'}
              content={content}
              darkMode={darkMode}
              inputting={status == 'running'}
            />
          </ScrollView>
        </div>
      );
    }, [content, status, index, darkMode]);

    return node;
  },
  (prevProps, nextProps) => {
    return (
      prevProps.content === nextProps.content &&
      prevProps.status === nextProps.status && 
      prevProps.darkMode === nextProps.darkMode
    );
  }
);

const CardPpt = observer(props => {
  const { data, mcpStore, type = 'agent', event } = props;
  const { contents, msg_id = '', status, url, share_url } = event || {};

  const { isMcpShare } = useConversationContext();
  const [viewState, setViewState] = useState({
    currentVal: 'code',
    pageNO: 1,
    isPhone: HelperCore.isPhone(),
  });
  const { currentVal, pageNO, isPhone } = viewState;

  const refScroll = useRef(null);
  const refIframe = useRef(null);
  const [shareId, setShareId] = useState('');
  const isDark = true;

  // 下载提示项
  const [downloadItems, setDownloadItems] = useState(DOWNLOAD_ITEMS);
  // 下载下拉框是否收起
  const [isShowDownload, setIsShowDownload] = useState(false);
  // 下载中项: 解决每项可以独立显示“下载中”
  const [downloadingKeys, setDownloadingKeys] = useState([]);

  // const [mock, setMock] = useState('');
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setMock(prev => {
  //       if (prev.length > 6300) {
  //         return prev;
  //       }
  //       return (
  //         prev + '000111222333444555666777888999aaabbbcccdddeeefffggghhh\n'
  //       );
  //     });
  //   }, 500);
  //   return () => clearInterval(interval);
  // }, []);
  // const contents = useMemo(() => {
  //   return [
  //     mock,
  //     mock,
  //     mock,
  //     mock,
  //     mock,
  //     mock,
  //     mock,
  //     mock,
  //     mock,
  //     mock,
  //     mock,
  //     mock,
  //     mock,
  //   ];
  // }, [mock]);

  // 虚拟化器配置
  const rowVirtualizer = useVirtualizer({
    count: contents?.length ?? 1, // 总数据量
    getScrollElement: () => refScroll.current, // 绑定滚动容器
    estimateSize: () => 300, // 每项高度（固定）
    overscan: 2, // 预渲染的行数
  });

  const actionItems = useMemo(
    () => [
      {
        label: 'PPT结果',
        value: 'result_ppt',
        disabled: !(status == 'success' || status == 'failed'),
        designed: ['result_ppt'],
      },
      {
        label: '源代码',
        value: 'code',
        disabled: status == 'failed',
        designed: [
          'result_html',
          'result_chart',
          'result_mindmap',
          'result_ppt',
        ],
      },
    ],
    [status]
  );

  useEffect(() => {
    if (!share_url) {
      return;
    }

    const params = new URLSearchParams(share_url);
    const id = params.get('id');
    if (id) {
      setShareId(id);
    } else {
      const host = share_url;
      if (/^https\:\/\/(\w{6,})\.n\.cn/.test(host)) {
        setShareId(RegExp.$1);
      }
    }
  }, [share_url]);

  useEffect(() => {
    setViewState(prev => ({
      ...prev,
      currentVal:
        status === 'success' || status === 'failed' ? 'result_ppt' : 'code',
    }));
  }, [status]);

  const handleCopyFile = useCallback(
    (input = '') => {
      if (status !== 'success') {
        Other.toast('PPT结果生成中，请稍后再试', 'error');
        return;
      }

      const plainText = markdownToPlainText(input);
      const copied = copy(plainText);
      copied && Other.toast('复制成功', 'success');
    },
    [status]
  );

  const handleShareFile = useCallback(() => {
    const copied = copy(share_url || url);
    copied && Other.toast('链接已复制', 'success');

    // request
    //   .post('/share/gen', {
    //     cid: mcpStore.chatStore.chatId,
    //     msg_id,
    //     type: 7,
    //   })
    //   .then(res => {
    //     const { id } = res?.data;
    //     if (id) {
    //       let url = `${location.protocol}//${id}.n.cn`;
    //       if (['dev', 'test'].includes(import.meta.env.MODE)) {
    //         url = `${location.protocol}//${location.host}/share/html?id=${id}`;
    //       }
    //       const copied = copy(url);
    //       copied && Other.toast('分享链接已复制', 'success');
    //     }
    //   })
    //   .catch(err => {
    //     Other.toast('分享失败，请重试', 'error');
    //   });
  }, [share_url, url]);

  const handlePageChange = useCallback(page => {
    setViewState(prevState => ({
      ...prevState,
      pageNO: page,
    }));
  }, []);

  const onSegmentedChange = useCallback(value => {
    setViewState(prevState => ({
      ...prevState,
      currentVal: value,
    }));
    console.log('onSegmentedChange', new Date().getTime(), value);
  }, []);
  
  const handleDownloadFile = (export_type = 'html', disabled = false) => {
    if (disabled || downloadingKeys.includes(export_type)) {
      // Other.toast('正在下载中，请稍后再试', 'warning');
      return;
    }

    // 1. 加入 downloadingKeys 列表
    setDownloadingKeys(prev => [...prev, export_type]);

    // 2. 显示 UI 状态
    setDownloadItems(prevItems =>
      prevItems.map(item => ({
        ...item,
        label:
          downloadingKeys.includes(item.key) || item.key === export_type
            ? `${item.key}下载中...`
            : DOWNLOAD_ITEMS.find(d => d.key === item.key)?.label,
        disabled:
          downloadingKeys.includes(item.key) || item.key === export_type,
      }))
    );

    setIsShowDownload(false);

    // 3. 发起下载请求
    request
      .post(
        '/ai_agent/msg/result/export',
        {
          conversation_id: mcpStore.chatStore.chatId,
          msg_id,
          type: 2,
          export_type,
        },
        { dataIsJson: true }
      )
      .then(res => {
        const { url = '' } = res?.data ?? {};
        const _fileName = `ppt-${mcpStore.chatStore.chatId}-${msg_id}.zip`;
        HelperCore.saveFile(url, _fileName);
      })
      .catch(() => {
        Other.toast('下载失败，请重试', 'error');
      })
      .finally(() => {
        // 4. 下载完成，移除 key
        setDownloadingKeys(prev => {
          const next = prev.filter(k => k !== export_type);

          // 5. 更新 UI
          setDownloadItems(prevItems =>
            prevItems.map(item => ({
              ...item,
              label: next.includes(item.key)
                ? `${item.key}下载中...`
                : DOWNLOAD_ITEMS.find(d => d.key === item.key)?.label,
              disabled: next.includes(item.key),
            }))
          );

          return next;
        });
      });
  };

  const html = useMemo(() => {
    const content = contents?.[pageNO - 1] || '';
    const html = HtmlViewTemplate.replace('{html_content_holder}', content);
    return html;
  }, [contents, pageNO]);

  const darkMode = currentVal == 'code' || mcpStore.isDarkMode;
  const empty_content = !Array.isArray(contents) || contents.length === 0;
  return (
    <ConfigProvider locale={zhCN}>
      <section
        className={[
          'overflow-hidden rounded-10px',
          'py-16px flex flex-col relative z-1 ',
          !darkMode ? 'bg-white' : 'bg-[#1E1F22]',
          empty_content ? 'min-h-260px' : '',
        ]}
      >
        {
          <div className="w-full h-full flex flex-col relative">
            <header className="px-24px pb-16px tabs border-bottom-0px border-[#e6e6e6] flex justify-left items-center">
              {mcpStore.hasHtmlFinished || true ? (
                <Segmented
                  options={actionItems}
                  value={currentVal}
                  onChange={onSegmentedChange}
                />
              ) : null}
              <div className="w-0 flex-grow"></div>

              {currentVal === 'result_ppt' && status === 'success' ? (
                <>
                  <div 
                    className={`
                      h-28px box-border rounded-6px border-1px border-black
                      border-opacity-16 leading-26px px-10px cursor-pointer 
                      hover:bg-[rgba(0,0,0,0.03)] mr-8px text-14px text-black
                      flex items-center
                      ${HelperCore.isPhone() ? "border-0px px-0px" : ""}
                    `}
                    onClick={() => {
                      if(isMcpShare && shareId) {
                        fullScreenModalStore.setData({
                          show: true, 
                          type: "ppt",
                          data: {
                            shareUrl: share_url || url,
                            contents: contents,
                            shareId: shareId,
                            chatId: mcpStore.chatStore.chatId,
                            msg_id: msg_id
                          }
                        });
                        return
                      }
                      fullScreenModalStore.setData({
                        show: true, 
                        type: "ppt",
                        data: {
                          shareUrl: share_url || url,
                          contents: contents,
                          chatId: mcpStore.chatStore.chatId,
                          msg_id: msg_id
                        }
                      });
                    }}
                  >
                    <SVGPureIcon
                      name="full_screen_modal_full2"
                      className={`size-16px ${!HelperCore.isPhone() ? "mr-6px" : ""}`}
                    />
                    {
                      !HelperCore.isPhone()
                      ? "全屏"
                      : null
                    }
                  </div>
                  {share_url ? (
                    <Tooltip 
                      title={
                        HelperCore.isPhone()
                        ? null
                        : <QrcodeShare url={share_url || url} />
                      } 
                      color="white"
                      placement="bottomLeft"
                      arrow={false}
                      getPopupContainer={node => node}
                      destroyTooltipOnHide
                    >
                      <div 
                        className={`
                          h-28px box-border rounded-6px border-1px border-black 
                          border-opacity-16 leading-26px px-10px cursor-pointer
                          hover:bg-[rgba(0,0,0,0.03)] mr-8px text-14px text-black
                          flex items-center
                          ${HelperCore.isPhone() ? "border-0px px-0px" : ""}
                        `}
                        onClick={() => {
                          const copied = copy(share_url || url);     
                          copied && Other.toast('复制成功', "success");
                        }}
                      >
                        <SVGPureIcon
                          name="full_screen_modal_share2"
                          className={`size-16px ${!HelperCore.isPhone() ? "mr-6px" : ""}`}
                        />
                        {
                          !HelperCore.isPhone()
                          ? "分享"
                          : null
                        }
                      </div>
                    </Tooltip>
                  ) : null}
                  <Dropdown
                    dropdownRender={() => (
                      <div className="flex flex-col w-132px h-136px bg-white rounded-12px p-8px shadow-2xl">
                        {downloadItems.map(item => (
                          <div
                            key={item.key}
                            className="w-116px h-40px px-8px py-13px rounded-6px cursor-pointer hover:bg-[#f7f7f7] text-black text-14px"
                            style={{
                              opacity: item.disabled ? 0.6 : 1,
                              cursor: item.disabled ? 'not-allowed' : 'pointer',
                            }}
                            onClick={() =>
                              handleDownloadFile(item.key, item.disabled)
                            }
                          >
                            {item.label}
                          </div>
                        ))}
                      </div>
                    )}
                    placement="bottom"
                    open={isShowDownload}
                    onOpenChange={visible => setIsShowDownload(visible)}
                  >
                    <div className='size-28px flex items-center justify-center hover:bg-[#f7f7f7] rounded-6px cursor-pointer'>
                      <SVGPureIcon
                        name="full_screen_modal_download"
                        className="size-24px"
                      />
                    </div>
                  </Dropdown>
                </>
              ) : null}
            </header>
            <div
              className={[
                `${!isPhone ? 'px-24px' : ''}`,
                !darkMode ? '' : '!pr-4px',
              ]}
            >
              {status === 'failed' ? (
                <div className="flex flex-col h-400px items-center justify-center">
                  <img
                    className="w-190px"
                    src="https://p5.ssl.qhimg.com/t11098f6bcda6a604f3f799138d.png"
                  />
                  <div className="text-14px leading-30px text-black text-opacity-50">
                    创建网站失败
                  </div>
                </div>
              ) : empty_content ? (
                <Skeleton
                  className={[
                    darkMode ? 'antd-dark' : '',
                    'mt-12px',
                  ]}
                  active
                />
              ) : currentVal === 'result_ppt' ? (
                isMcpShare && shareId ? (
                  <div
                    className={`flex-1 overflow-hidden mt-8px`}
                    ref={refScroll}
                  >
                    <ScaleBox>
                      <iframe
                        ref={refIframe}
                        src={`/api/html/detail?id=${shareId}&page_index=${pageNO-1}`}
                        className="w-1280px h-720px"
                        sandbox="allow-scripts allow-same-origin allow-popups allow-downloads allow-modals allow-popups-to-escape-sandbox"
                      />
                    </ScaleBox>
                  </div>
                ) : (
                  <div ref={refScroll} className={`overflow-hidden mt-8px`}>
                    <ScaleBox>
                      <iframe
                        ref={refIframe}
                        className="w-1280px h-720px"
                        srcDoc={html}
                        src="about:srcdoc"
                        sandbox="allow-scripts allow-same-origin allow-popups allow-downloads allow-modals allow-popups-to-escape-sandbox"
                      />
                    </ScaleBox>
                  </div>
                )
              ) : (
                <ScrollView
                  ref={refScroll}
                  scrollbarThin={true}
                  enableAutoScroll={true}
                  className="!h-min-700px !h-max-700px"
                >
                  {
                    /* 虚拟列表 */
                    enableVirtual && (
                      <div
                        style={{
                          height: `${rowVirtualizer.getTotalSize()}px`, // 计算总高度
                          width: '100%',
                          position: 'relative',
                        }}
                      >
                        {/* 仅渲染可见项 */}
                        {rowVirtualizer.getVirtualItems().map(virtualItem => {
                          const index = virtualItem.index;
                          const content = contents[virtualItem.index] || '';
                          return (
                            <div
                              key={virtualItem.key}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: `${virtualItem.size}px`, // 每项高度
                                transform: `translateY(${virtualItem.start}px)`, // 定位
                              }}
                            >
                              <CodeCard
                                key={`${msg_id}-${index}`}
                                index={index}
                                msg_id={msg_id}
                                isPhone={isPhone}
                                handleCopyFile={handleCopyFile}
                                status={status}
                                content={content}
                                darkMode={darkMode}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )
                  }
                  {!enableVirtual &&
                    contents?.map((content, index) => {
                      return (
                        <Suspense fallback={null}>
                          <CodeCard
                            key={`${msg_id}-${index}`}
                            index={index}
                            msg_id={msg_id}
                            isPhone={isPhone}
                            handleCopyFile={handleCopyFile}
                            status={status}
                            content={content}
                            darkMode={darkMode}
                          />
                        </Suspense>
                      );
                    })}
                </ScrollView>
              )}
            </div>

            {currentVal === 'result_ppt' && !empty_content ? (
              <div className="flex justify-end items-center mt-10px mr-24px ">
                <Pagination
                  pageSize={1}
                  current={pageNO}
                  simple={{
                    readOnly: true,
                  }}
                  total={contents?.length || 0}
                  onChange={handlePageChange}
                  itemRender={(current, type, originalElement) => {
                    const isFirstPage = pageNO === 1;
                    const isLastPage = pageNO >= (contents?.length || 0);
                    if (type === 'prev') {
                      return (
                        <a
                          className={[
                            'flex items-center !text-[#000] rounded-6px',
                            'border-1 border-[rgba(0,0,0,0.16)] hover:bg-[#f7f7f7] hover:border-opacity-10 pl-2px pr-4px',
                            isFirstPage
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:text-blue-500',
                          ]}
                          onClick={e => isFirstPage && e.preventDefault()}
                        >
                          <LeftOutlined />
                          <span>上一页</span>
                        </a>
                      );
                    }
                    if (type === 'next') {
                      return (
                        <a
                          className={[
                            'flex items-center !text-[#000] rounded-6px',
                            'border-1 border-[rgba(0,0,0,0.16)] hover:bg-[#f7f7f7] hover:border-opacity-10 pl-4px pr-2px',
                            isLastPage
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:text-blue-500',
                          ]}
                          onClick={e => isLastPage && e.preventDefault()}
                        >
                          <span>下一页</span>
                          <RightOutlined />
                        </a>
                      );
                    }
                    return originalElement;
                  }}
                />
              </div>
            ) : null}
          </div>
        }
      </section>
    </ConfigProvider>
  );
});

export default CardPpt;
